import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SITE_NAME, DEFAULT_TITLE, DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE, SITE_URL, pageSeo } from 'constants/seo';

// Every route renders one of these. It's the only thing standing between
// Google and a site where every page shares the same <title> and
// description.
//
// A fixed page needs no props at all: the copy for it lives in
// constants/seo.json, keyed by pathname, and is looked up here. That table is
// also what scripts/prerender.js reads to write the same title, description
// and canonical into the static HTML — so the page a crawler sees before it
// runs any JavaScript says exactly what this component says afterwards.
// Pages built from fetched content (an article, a competition) aren't in the
// table and pass their own title/description/image instead.
//
// `path` overrides the canonical/og:url path — for a route that is a second
// address for a page that already has one (/ballroom-dance/news is /news), and
// for pages that render before their real path resolves. Everything else
// derives it from the router.
export const Seo = ({ title, description, path, image = DEFAULT_OG_IMAGE, type = 'website', noindex }) => {
  const { pathname } = useLocation();
  const page = pageSeo(pathname);

  const pageTitle = (title ?? page.title) ? `${title ?? page.title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const pageDescription = description ?? page.description ?? DEFAULT_DESCRIPTION;
  const url = `${SITE_URL}${path ?? page.path ?? pathname}`;
  const isNoindex = noindex ?? page.noindex ?? false;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name='description' content={pageDescription} />
      <link rel='canonical' href={url} />
      {isNoindex && <meta name='robots' content='noindex, nofollow' />}

      <meta property='og:type' content={type} />
      <meta property='og:site_name' content={SITE_NAME} />
      <meta property='og:title' content={pageTitle} />
      <meta property='og:description' content={pageDescription} />
      <meta property='og:url' content={url} />
      <meta property='og:image' content={image} />

      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={pageTitle} />
      <meta name='twitter:description' content={pageDescription} />
      <meta name='twitter:image' content={image} />
    </Helmet>
  );
};
